// The compose file for a worker running on ANOTHER host, in one place.
//
// Two paths write this file: the copy-paste snippet an operator runs by hand,
// and the SSH deploy that writes it over a pipe. If they generated it
// separately they would drift, and the documented manual procedure would stop
// describing what the automated one does. So generation lives here, has no HTTP
// or filesystem concerns, and both callers read it.
//
// Two things make this more than a `docker run` line:
//
//   Release origin — a remote worker cannot resolve the manager's docker
//   container name, so it needs a publicly reachable origin. remoteReleaseOrigin()
//   in worker-deploy-config.ts refuses rather than guessing; this module only
//   renders whatever it was handed.
//
//   Auto-update — a container cannot pull and recreate itself, so rolling a
//   remote worker needs a small updater-agent sidecar beside it.

import { z } from "zod";

/** Port the updater-agent sidecar listens on. */
export const AGENT_PORT = 3999;

/** Port local-api listens on, published on the remote host. */
export const WORKER_PORT = 38923;

/**
 * Compose project name. The updater-agent is told this so its
 * `docker compose up` targets the same project the deploy created; a mismatch
 * makes a roll create a second, parallel stack.
 */
export const COMPOSE_PROJECT_NAME = "veil-worker";

/** File name the copy-paste snippet tells the operator to write. */
export const DEFAULT_COMPOSE_FILE_NAME = "veil-worker.yaml";

/** Named volume holding the worker's install root (chromium + profiles). */
export const INSTALL_VOLUME_NAME = "veil-install";

const ORG_KEY_PLACEHOLDER = "<your-org-key>";
const RELEASE_ORIGIN_PLACEHOLDER = "https://release.<your-domain>";

export const RemoteWorkerComposeInfoSchema = z.object({
	/**
	 * Worker repository, already resolved (no tag).
	 *
	 * Resolved by the caller rather than reassembled here from a registry host
	 * and an org key. Building it in two places is how the remote deploy came to
	 * ignore WORKER_IMAGE_REPO while the local one honoured it: an operator
	 * pointing the cluster at an internal mirror got remote workers pulled from
	 * the public gate instead, which either fails or quietly deploys a different
	 * image than every other worker in the fleet.
	 */
	workerImageRepo: z.string(),
	workerImageTag: z.string(),
	/** Updater-agent repository (no tag). See updaterAgentImageRepo. */
	agentImageRepo: z.string(),
	/**
	 * Updater-agent tag. Independent of the worker's: the two are released on
	 * separate cadences, so the worker's tag names an agent that may not exist.
	 */
	agentImageTag: z.string(),
	saasApiUrl: z.string(),
	/** Publicly reachable release origin, or null when none is configured yet. */
	releaseOrigin: z.string().nullable(),
});
export type RemoteWorkerComposeInfo = z.infer<
	typeof RemoteWorkerComposeInfoSchema
>;

/**
 * How secrets appear in the rendered file.
 *
 *   placeholder — copy-paste path. `VEIL_KEY` is a literal `<your-org-key>` the
 *                 operator fills in; the agent token is inlined because they
 *                 are reading it off their own screen anyway.
 *   env-file    — SSH path. Secrets become `${VAR}` interpolations resolved from
 *                 the sibling `.env`, so they never enter the compose file and
 *                 cannot be read out of it by anything that can list /opt.
 */
export const ComposeSecretModeSchema = z.enum(["placeholder", "env-file"]);
export type ComposeSecretMode = z.infer<typeof ComposeSecretModeSchema>;

export const RemoteWorkerComposeOptionsSchema = z.object({
	info: RemoteWorkerComposeInfoSchema,
	hostname: z.string(),
	/** Emit the updater-agent sidecar beside the worker. */
	autoUpdate: z.boolean().default(false),
	/** Shared secret the manager presents to the agent. Ignored without autoUpdate. */
	token: z.string().default(""),
	secretMode: ComposeSecretModeSchema.default("placeholder"),
	/**
	 * Name the file is written under. It appears in the header comment and in
	 * the agent's bind mount, so the agent's `COMPOSE_FILE` always points at the
	 * file that actually exists.
	 */
	composeFileName: z.string().default(DEFAULT_COMPOSE_FILE_NAME),
	/** Operator-set geo pool, emitted as VEIL_REGION when present. */
	region: z.string().nullable().optional(),
});
export type RemoteWorkerComposeOptions = z.input<
	typeof RemoteWorkerComposeOptionsSchema
>;

/** Fully qualified worker image reference for a remote host. */
export function remoteWorkerImageRef(info: RemoteWorkerComposeInfo): string {
	return `${info.workerImageRepo}:${info.workerImageTag}`;
}

/** Fully qualified updater-agent image reference for a remote host. */
export function remoteAgentImageRef(info: RemoteWorkerComposeInfo): string {
	return `${info.agentImageRepo}:${info.agentImageTag}`;
}

/**
 * Render the compose file for a remote worker.
 *
 * Byte-for-byte identical to what the copy-paste snippet has always produced
 * when called with the defaults, so moving generation server-side does not
 * change the file operators are already running.
 */
export function renderRemoteWorkerCompose(
	options: RemoteWorkerComposeOptions,
): string {
	const {
		info,
		hostname,
		autoUpdate,
		token,
		secretMode,
		composeFileName,
		region,
	} = RemoteWorkerComposeOptionsSchema.parse(options);

	const origin = info.releaseOrigin ?? RELEASE_ORIGIN_PLACEHOLDER;
	const orgKeyValue =
		secretMode === "env-file" ? "${VEIL_KEY}" : ORG_KEY_PLACEHOLDER;
	const agentTokenValue = secretMode === "env-file" ? "${AGENT_TOKEN}" : token;

	const lines = [
		`# ${composeFileName} — run with: docker compose -f ${composeFileName} up -d`,
		"services:",
		"  local-api:",
		`    image: ${remoteWorkerImageRef(info)}`,
		`    container_name: veil-worker-${hostname}`,
		"    restart: unless-stopped",
		"    shm_size: 1g",
		"    tmpfs: [/tmp]",
		"    ports:",
		`      - "${WORKER_PORT}:${WORKER_PORT}"`,
		"    volumes:",
		`      - ${INSTALL_VOLUME_NAME}:/var/veil/install`,
		"    environment:",
		`      VEIL_KEY: ${orgKeyValue}`,
		`      VEIL_WORKER_ID: ${hostname}`,
		`      SAAS_API_URL: ${info.saasApiUrl}`,
		`      RELEASE_ORIGIN_URL: ${origin}`,
		"      VEIL_BOOTSTRAP_RELEASES: 1",
	];
	if (region) lines.push(`      VEIL_REGION: ${region}`);

	if (autoUpdate) {
		lines.push(
			"",
			"  # Lets cluster-manager roll this worker to a new image on request.",
			"  # A container cannot recreate itself, so this sidecar does it.",
			"  updater-agent:",
			`    image: ${remoteAgentImageRef(info)}`,
			`    container_name: veil-updater-${hostname}`,
			"    restart: unless-stopped",
			"    environment:",
			`      AGENT_TOKEN: ${agentTokenValue}`,
			`      COMPOSE_PROJECT: ${COMPOSE_PROJECT_NAME}`,
			`      COMPOSE_FILE: /work/${composeFileName}`,
			"      COMPOSE_SERVICE: local-api",
			"      AGENT_ENV_FILE: /work/.env",
			`      WORKER_HEALTH_URL: http://local-api:${WORKER_PORT}/health`,
			"    ports:",
			`      - "${AGENT_PORT}:${AGENT_PORT}"`,
			"    volumes:",
			"      - /var/run/docker.sock:/var/run/docker.sock",
			`      - ./${composeFileName}:/work/${composeFileName}:ro`,
			"      - ./.env:/work/.env",
		);
	}

	lines.push("", "volumes:", `  ${INSTALL_VOLUME_NAME}:`);
	return lines.join("\n");
}

export const RemoteWorkerEnvFileOptionsSchema = z.object({
	/** The org key, verbatim (not lowercased — that is only the registry path). */
	orgKey: z.string(),
	/** Agent token, when the updater-agent is part of the deploy. */
	agentToken: z.string().nullable().default(null),
});
export type RemoteWorkerEnvFileOptions = z.input<
	typeof RemoteWorkerEnvFileOptionsSchema
>;

/**
 * Render the `.env` that sits beside the compose file on the remote host.
 *
 * Only used by the SSH path (`secretMode: "env-file"`). Docker Compose reads
 * `.env` from the project directory for interpolation, so this is what resolves
 * the `${VEIL_KEY}` / `${AGENT_TOKEN}` references. The updater-agent also mounts
 * it read-write as its `AGENT_ENV_FILE`.
 */
export function renderRemoteWorkerEnvFile(
	options: RemoteWorkerEnvFileOptions,
): string {
	const { orgKey, agentToken } =
		RemoteWorkerEnvFileOptionsSchema.parse(options);

	const lines = [
		"# Written by VeilBrowser cluster-manager. Secrets — mode 0600.",
		`VEIL_KEY=${orgKey}`,
	];
	if (agentToken) lines.push(`AGENT_TOKEN=${agentToken}`);
	return `${lines.join("\n")}\n`;
}

/**
 * 32 bytes of hex — the shared secret cluster-manager presents to the agent.
 * Uses WebCrypto so the same function works in the browser and under Bun.
 */
export function mintAgentToken(): string {
	const bytes = new Uint8Array(32);
	crypto.getRandomValues(bytes);
	return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}
